/**
 * User service
 * Handles Firestore Users collection operations
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { UserProfile } from '../../../shared/types';
import { db, functions } from '../config/firebase';
import { convertDocumentTimestamps } from '@shared/utils/firestore';

/**
 * Create a new user profile in Firestore
 */
export const createUserProfile = async (
  uid: string,
  data: Omit<UserProfile, 'uid'>
): Promise<void> => {
  await setDoc(doc(db, 'Users', uid), {
    ...data,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
};

/**
 * Get user profile by UID
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const userDoc = await getDoc(doc(db, 'Users', uid));

  if (!userDoc.exists()) {
    return null;
  }

  return {
    uid: userDoc.id,
    ...convertDocumentTimestamps<UserProfile>(userDoc.data()),
  } as UserProfile;
};

/**
 * Get all Manager and Staff users for a company (excluding archived by default)
 */
export const getCompanyUsers = async (
  companyId: string,
  includeArchived: boolean = false
): Promise<UserProfile[]> => {
  const constraints = [
    where('companyId', '==', companyId),
    where('userType', 'in', ['manager', 'staff']),
  ];

  // Exclude archived users by default
  if (!includeArchived) {
    constraints.push(where('isArchived', '==', false));
  }

  const usersQuery = query(collection(db, 'Users'), ...constraints);
  const snapshot = await getDocs(usersQuery);

  return snapshot.docs.map((doc) => ({
    uid: doc.id,
    ...convertDocumentTimestamps<UserProfile>(doc.data()),
  })) as UserProfile[];
};

/**
 * Get company administrators
 */
export const getCompanyAdministrators = async (companyId: string): Promise<UserProfile[]> => {
  const adminsQuery = query(
    collection(db, 'Users'),
    where('companyId', '==', companyId),
    where('userType', '==', 'company')
  );
  const snapshot = await getDocs(adminsQuery);

  return snapshot.docs.map((doc) => ({
    uid: doc.id,
    ...convertDocumentTimestamps<UserProfile>(doc.data()),
  })) as UserProfile[];
};

/**
 * Get all users (optionally excluding archived)
 */
export const getAllUsers = async (includeArchived: boolean = false): Promise<UserProfile[]> => {
  const constraints = includeArchived ? [] : [where('isArchived', '==', false)];
  const usersQuery = constraints.length > 0
    ? query(collection(db, 'Users'), ...constraints)
    : collection(db, 'Users');

  const snapshot = await getDocs(usersQuery);
  return snapshot.docs.map((doc) => ({
    uid: doc.id,
    ...convertDocumentTimestamps<UserProfile>(doc.data()),
  })) as UserProfile[];
};

/**
 * Get archived users for a company
 */
export const getArchivedUsers = async (companyId: string): Promise<UserProfile[]> => {
  const usersQuery = query(
    collection(db, 'Users'),
    where('companyId', '==', companyId),
    where('userType', 'in', ['manager', 'staff']),
    where('isArchived', '==', true)
  );
  const snapshot = await getDocs(usersQuery);

  return snapshot.docs.map((doc) => ({
    uid: doc.id,
    ...convertDocumentTimestamps<UserProfile>(doc.data()),
  })) as UserProfile[];
};

/**
 * Get staff members managed by a specific manager
 */
export const getManagerStaff = async (managerId: string): Promise<UserProfile[]> => {
  // First get the manager's profile to access managedStaffIds
  const managerProfile = await getUserProfile(managerId);

  if (!managerProfile || !managerProfile.managedStaffIds || managerProfile.managedStaffIds.length === 0) {
    return [];
  }

  // Fetch all staff profiles in batches (Firestore IN query limit is 10)
  const staffIds = managerProfile.managedStaffIds;
  const staffProfiles: UserProfile[] = [];

  // Process in chunks of 10
  for (let i = 0; i < staffIds.length; i += 10) {
    const chunk = staffIds.slice(i, i + 10);

    // Fetch each staff member by document ID
    const fetchPromises = chunk.map(async (staffId) => {
      const staffDoc = await getDoc(doc(db, 'Users', staffId));
      if (staffDoc.exists()) {
        return {
          uid: staffDoc.id,
          ...convertDocumentTimestamps<UserProfile>(staffDoc.data()),
        } as UserProfile;
      }
      return null;
    });

    const results = await Promise.all(fetchPromises);
    const validProfiles = results.filter((profile): profile is UserProfile => profile !== null);
    staffProfiles.push(...validProfiles);
  }

  return staffProfiles;
};

/**
 * Archive a user (soft delete)
 * Calls the Cloud Function to archive the user
 */
export const archiveUser = async (userId: string, companyId: string): Promise<void> => {
  const archiveUserFn = httpsCallable(functions, 'archiveUser');
  await archiveUserFn({ userId, companyId });
};

/**
 * Restore a user from archive
 * Calls the Cloud Function to restore the user
 */
export const restoreUser = async (userId: string, companyId: string): Promise<void> => {
  const restoreUserFn = httpsCallable(functions, 'restoreUser');
  await restoreUserFn({ userId, companyId });
};

/**
 * Delete a user permanently (both Auth and Firestore) and decrement company's currentUsers
 * Calls the Cloud Function which uses Admin SDK to delete both Firebase Auth and Firestore
 * Should only be used after archiving first (or for immediate deletion if needed)
 */
export const deleteUser = async (userId: string, companyId: string): Promise<void> => {
  const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
  await deleteUserAccount({ userId, companyId });
};

/**
 * Update manager's staff assignments
 * Calls the Cloud Function to update which staff members are assigned to a manager
 */
export const updateManagerStaff = async (
  managerId: string,
  companyId: string,
  managedStaffIds: string[]
): Promise<void> => {
  const updateManagerStaffFn = httpsCallable(functions, 'updateManagerStaff');
  await updateManagerStaffFn({ managerId, companyId, managedStaffIds });
};

export interface UpdateUserProfileInput {
  targetUserId: string;
  companyId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  countryCode: string;
  email?: string;
}

export const updateUserProfile = async (input: UpdateUserProfileInput): Promise<void> => {
  const fn = httpsCallable(functions, 'updateUserProfile');
  await fn(input);
};
